<template>
  <div
    class="line-item"
    :class="{
      [$class('loading')]: !lineItem,
      [$class('updating')]: state === 'updating',
    }"
  >
    <transition
      name="fade"
      :duration="$timing('quick')"
    >
      <p
        v-if="isGift && giftAdded"
        class="line-item__messages line-item__messages-top"
        v-text="$string('cart.free_gift.line_item_title')"
      />
    </transition>

    <div class="line-item__grid">
      <component
        :is="dynamicTag"
        class="line-item__image-container"
        :href="dynamicTag === 'a' ? lineItem?.url : null"
      >
        <responsive-image
          class="line-item__image"
          :image="lineItem.featured_image"
          :lazyload="false"
          min-max="95-285"
          ratio="1:1"
          :sizes="`(min-width: ${breakpoints.l}) 99px, 95px`"
        />
      </component>

      <div class="line-item__body">
        <div class="line-item__details">
          <component
            :is="dynamicTag"
            class="
              line-item__title
              text-body-m-regular
            "
            :href="dynamicTag === 'a' ? lineItem.url : null"
          >
            {{ formatProductTitle(lineItem.product_title) }}
          </component>

          <template v-if="variantOptions && !isGift">
            <p
              v-for="option of variantOptions"
              :key="option"
              class="
                line-item__variant
                text-body-s-regular
              "
              v-text="option"
            />
          </template>
          <template v-if="isGift">
            <p class="text-body-s-regular">
              <span
                v-if="$variable('gwp.displayPrice')"
                class="line-item__line-through"
              >
                {{ formatCurrency(lineItem?.original_price) }}
              </span>
              {{ $string('cart.free_gift.line_item_copy') }}
            </p>
          </template>

          <template v-else>
            <p class="line-item__price-container text-body-s-regular">
              <product-prices
                class="line-item__price"
                class-price="text-body-s-regular"
                class-compare-at-price="text-body-s-regular"
                :compare-at-price="compareAtPrice"
                element="span"
                :price="lineItem?.final_price"
              />
            </p>
          </template>

          <!-- Discount -->
          <p
            v-if="!isGift"
            class="line-item__discounts text-body-s-regular"
          >
            <span
              v-for="({ discount_application }) of lineItem.line_level_discount_allocations"
              :key="`discount-${discount_application.key}`"
              class="line-item__discount"
            >
              <icon-tag />
              {{ discount_application.title }}
            </span>
          </p>
        </div>

        <div class="line-item__actions">
          <quantity-selector
            v-if="!isGift"
            class="line-item__quantity-selector"
            :line-item="lineItem"
            :quantity="quantity"
            :state="state"
            @update-quantity="handleQuantityUpdate"
          />

          <button
            class="line-item__remove text-body-s-underline"
            :class="{ [$class('disabled')]: state === 'updating' }"
            type="button"
            @click="handleRemove"
            v-text="$string('cart.line_item.remove')"
          />
        </div>
      </div>
    </div>

    <div
      v-if="(notification.display || properties.length) && !isGift"
      class="line-item__messages"
    >
      <p
        v-if="notification.display"
        class="text-body-xs-regular"
        v-text="notification.text"
      />

      <p
        v-for="prop of properties"
        :key="prop.key"
        class="text-body-xs-regular"
        :style="{ color: isMadeToOrder ? 'var(--color-neutral-black)' : '' }"
        v-text="`${prop.key}: ${prop.value}`"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Line item (line-item)
 * -----------------------------------------------------------------------------
 * Displays line items in cart, cart drawer, and order.
 *
 * @param {Boolean|Object} lineItem - Line item in cart response or order.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import ProductPrices from '~global/product-prices/product-prices'
import QuantitySelector from '~async/quantity-selector/quantity-selector'
import ResponsiveImage from '~global/images/responsive-image'

import IconTag from '~icons/general/tag.svg'

import { formatProductTitle } from '~/helpers/format'
import breakpoints from '~/config/breakpoints'

export default {
  name: 'LineItem',

  components: {
    IconTag,
    ProductPrices,
    QuantitySelector,
    ResponsiveImage,
  },

  props: {
    lineItem: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    giftAdded: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      quantity: 1,
      state: 'loading',
      formatProductTitle,
      breakpoints,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      giftLineItem: 'cart/getGiftLineItem',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cartStatus: (state) => state.cart.status,
    }),

    /**
     * Compute compare at price.
     * - Uses compare at price if it exists.
     * - Otherwise if script discount has been applied use original price.
     * - This way it always shows the largest original price.
     * - Otherwise show no compare at price.
     * @returns {Number|Boolean}
     */
    compareAtPrice() {
      if (this.lineItem?.compare_at_price) {
        return this.lineItem?.compare_at_price
      }

      if (this.lineItem?.original_price !== this.lineItem?.final_price) {
        return this.lineItem?.original_price
      }

      return false
    },

    /**
     * Compute if line item is limited by inventory quantity.
     * @returns {Boolean}
     */
    limitedInventory() {
      return (
        this.lineItem.inventory_management &&
        this.lineItem.inventory_policy === 'deny'
      )
    },

    /**
     * Compute notification state.
     * @returns {Object}
     */
    notification() {
      if (!this.lineItem || !this.limitedInventory) {
        return { display: false }
      }

      const notification = {
        type: 'error',
      }

      if (this.lineItem.quantity > this.lineItem.inventory_quantity) {
        notification.display = true
        notification.title = this.$string('cart.notifications.limited_stock.title')

        notification.text = this.$string(
          'cart.notifications.limited_stock.text',
          {
            pluralise: this.lineItem.inventory_quantity,
            replace: { count: this.lineItem.inventory_quantity },
          },
        )
      } else if (this.lineItem.quantity === this.lineItem.inventory_quantity) {
        notification.display = true
        notification.title = this.$string('cart.notifications.maximum_quantity.title')

        notification.text = this.$string(
          'cart.notifications.maximum_quantity.text',
          {
            pluralise: this.lineItem.inventory_quantity,
            replace: { count: this.lineItem.inventory_quantity },
          },
        )
      }

      return notification
    },

    /**
     * Compute line item properties without hidden properties
     * @returns {{key: string, value: string}[]}
     */
    properties() {
      const properties = []
      if (!this.lineItem.properties) {
        return properties
      }
      Object.entries(this.lineItem.properties).forEach((entry) => {
        if (entry[0].startsWith('_')) {
          return
        }
        properties.push({
          key: entry[0],
          value: entry[1],
        })
      })

      return properties
    },

    /**
     * Compute variant options.
     * @returns {Array|null}
     */
    variantOptions() {
      const {
        gift_card,
        product_has_only_default_variant,
        options_with_values,
      } = this.lineItem || {}

      // eslint-disable-next-line camelcase
      if (gift_card || product_has_only_default_variant || !options_with_values) {
        return null
      }

      // eslint-disable-next-line camelcase
      return options_with_values.map((option) => {
        return this.$string(
          'cart.line_item.variant',
          {
            replace: {
              option_name: option.name,
              selected_value: option.value,
            },
          },
        )
      })
    },

    /**
     * Compute if line item is a gift.
     * @returns {Boolean}
     */
    isGift() {
      const { lineItem } = this
      const { gwp } = window.cnvs || { gwp: {} }
      // eslint-disable-next-line no-underscore-dangle
      return lineItem.product_id === gwp.product && lineItem.properties?._isGift
    },

    /**
     * Compute dynamic tag.
     * @returns {String}
     */
    dynamicTag() {
      return this.isGift ? 'span' : 'a'
    },

    /**
     * Compute if line item is made to order
     * @returns {boolean}
     */
    isMadeToOrder() {
      return this.lineItem.properties?._madeToOrder === 'true'
    },
  },

  watch: {

    /**
     * Watch cart status items to see if item is being updated.
     */
    'cartStatus.items'() {
      if (this.cartStatus.items.includes(this.lineItem.key)) {
        this.state = 'updating'
        return
      }

      this.state = 'ready'
    },

    /**
     * Watch item quantity to update quantity if updated elsewhere.
     */
    'lineItem.quantity'() {
      this.quantity = this.lineItem.quantity
    },
  },

  mounted() {
    this.state = 'ready'
    this.quantity = this.lineItem.quantity
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      updateItem: 'cart/updateItem',
      removeItem: 'cart/updateItem',
    }),

    /**  Format the currency using the money format from data
     * @param {Number} value - Currency value.
     * @returns {String}
     */
    formatCurrency(value) {
      return this.$formatMoney(value)
    },

    /**
     * Handle quantity update event.
     * @param {String} quantity - New quantity.
     */
    async handleQuantityUpdate(quantity) {
      this.quantity = quantity

      try {
        this.state = 'updating'

        await this.updateItem([
          {
            id: this.lineItem.key,
            quantity: this.quantity,
          },
        ])

        this.state = 'ready'
      } catch (error) {
        this.quantity = this.lineItem.quantity
        this.state = 'error'
      }
    },

    /**
     * Handle remove click.
     */
    handleRemove() {
      if (this.state === 'updating') {
        return
      }

      this.removeItem(this.lineItem.key)
    },
  },
}
</script>

<style lang="scss">
@import './line-item';
</style>
